/**
 * What the connector actually dials for each integration, so the
 * Troubleshooter's host list, prefill and ports test the same thing
 * (Jacob, 2026-09-27: "make sure the connectivity check ... is utilizing
 * everything we connect to").
 *
 * Mapped from the connector factory (vms-connector `connector_factories/
 * shared/factory.py`), the pullers/senders in actuate-libraries and admin's
 * onboarding-form defaults. A port left blank on the site falls back to the
 * default the connector uses, exactly as the connector does.
 *
 * Admin exports every is_rtsp integration (hikvision, axis, wisenet, …) as
 * `rtsp`; cortrol is exported as `luxriot`.
 */

export type EndpointRole =
  | "server"
  | "management_server"
  | "event_server"
  | "relay";

export interface SiteEndpoint {
  role: EndpointRole;
  address: string;
  ports: number[];
  label: string;
}

export interface EndpointProfile {
  /** Where the connector's traffic goes. */
  where: "site" | "vendor-cloud" | "inbound";
  /** Plain words for cloud and inbound sites: there is nothing at the site to reach. */
  note: string | null;
  /** Addresses on the site record the connector dials, with their ports. */
  endpoints: SiteEndpoint[];
  /** Ports to test on each camera address (RTSP-family and RTSP streams). */
  cameraPorts: number[];
  /** Ports to test on each Milestone/Salient recording server. */
  recordingServerPorts: number[];
}

export interface EndpointSite {
  integration_type_name?: string | null;
  server_ip: string | null;
  server_port?: number | null;
  stream_port?: number | null;
  management_server_ip?: string | null;
  event_server_ip?: string | null;
  alert_port?: number | null;
  milestone_http_port?: number | null;
  milestone_ssl_port?: number | null;
  cloud_system_id?: string | null;
  /** Digital Watchdog: the connector goes only through the Nx cloud relay. */
  use_nx_proxy?: boolean | null;
  /** OpenEye: the connector goes through OpenEye's cloud (OWS), not the NVR. */
  openeye_use_ows?: boolean | null;
  openeye_nvr_id?: string | null;
}

/** The OWS API host the connector uses (actuate-config openeye_config). */
export const OPENEYE_OWS_HOST = "actuate.api.gp4f.com";

/**
 * Star4Live and EZCloud save a URL ("http://www.star4live.com") in
 * server_ip. The host and the scheme's port are what the connector dials.
 */
export const urlHost = (
  value: string
): { host: string; port: number | null } => {
  const match = /^([a-z][a-z0-9+.-]*):\/\/([^/:?#]+)(?::(\d+))?/i.exec(value);
  if (!match) return { host: value, port: null };
  const scheme = match[1].toLowerCase();
  return {
    host: match[2],
    port: match[3]
      ? Number(match[3])
      : scheme === "https"
      ? 443
      : scheme === "http"
      ? 80
      : null,
  };
};

const port = (value: number | null | undefined, fallback: number | null) =>
  typeof value === "number" && value > 0 ? value : fallback;

const ports = (...values: (number | null | undefined)[]): number[] =>
  Array.from(
    new Set(values.filter((v): v is number => typeof v === "number" && v > 0))
  );

const text = (value: string | null | undefined) => (value || "").trim();

const SMTP = ["smtp_per_camera", "hikvision_smtp", "speco_smtp", "ajax"];
const CLIPS = ["ailink", "sentinel-verifier", "clips", "yoursix"];
const CLOUD: Record<string, string> = {
  eagle_eye: "Eagle Eye",
  eagle_eye_v3: "Eagle Eye",
  kvs: "Amazon Kinesis Video",
  ajax_video: "Ajax",
  autopatrol: "Immix AutoPatrol",
  vch: "Immix AutoPatrol",
  star4live: "Star4Live",
  ezcloud: "Uniview EZCloud",
};

export const endpointProfile = (site: EndpointSite | null): EndpointProfile => {
  const empty: EndpointProfile = {
    where: "site",
    note: null,
    endpoints: [],
    cameraPorts: [554, 80],
    recordingServerPorts: [],
  };
  if (!site) return empty;
  const name = text(site.integration_type_name).toLowerCase();
  const server = text(site.server_ip);
  const withServer = (ps: number[], label = "Site server"): SiteEndpoint[] =>
    server ? [{ role: "server", address: server, ports: ps, label }] : [];
  const eventServer = (): SiteEndpoint[] =>
    text(site.event_server_ip)
      ? [
          {
            role: "event_server",
            address: text(site.event_server_ip),
            // The connector opens a TCP socket per alarm (default 9090).
            ports: ports(port(site.alert_port, 9090)),
            label: "Event server (alarms)",
          },
        ]
      : [];
  const managementServer = (): SiteEndpoint[] =>
    text(site.management_server_ip)
      ? [
          {
            role: "management_server",
            address: text(site.management_server_ip),
            ports: ports(
              port(site.milestone_ssl_port, 443),
              port(site.milestone_http_port, 80)
            ),
            label: "Management server",
          },
        ]
      : [];

  if (SMTP.includes(name))
    return {
      ...empty,
      where: "inbound",
      note: "This site's cameras email their images to us, so there is nothing at the site for us to reach. Use the SMTP Cameras tab to check what has arrived.",
      cameraPorts: [],
    };
  if (CLIPS.includes(name))
    return {
      ...empty,
      where: "inbound",
      note: "This site's partner sends clips to us, so there is nothing at the site for us to reach from here.",
      cameraPorts: [],
    };
  if (CLOUD[name]) {
    const vendor = CLOUD[name];
    return {
      ...empty,
      where: "vendor-cloud",
      note: `This site streams through ${vendor}'s cloud, not from the site to us, so there is nothing at the site to test from here. If video stops, check the site's connection to ${vendor}.`,
      cameraPorts: [],
      endpoints:
        (name === "star4live" || name === "ezcloud") && server
          ? (() => {
              const { host, port: urlPort } = urlHost(server);
              return [
                {
                  role: "relay" as const,
                  address: host,
                  ports: [urlPort ?? 443],
                  label: `${vendor} server`,
                },
              ];
            })()
          : [],
    };
  }

  switch (name) {
    case "milestone":
      // JPEGs from each recording server's image server on the site's
      // stream port (default 7563); SOAP to the management server.
      return {
        ...empty,
        cameraPorts: [],
        endpoints: [...managementServer(), ...eventServer()],
        recordingServerPorts: ports(port(site.stream_port, 7563)),
      };
    case "milestone_rtsp":
      return {
        ...empty,
        cameraPorts: [554],
        endpoints: [...managementServer(), ...eventServer()],
      };
    case "digital_watchdog": {
      const cloudId = text(site.cloud_system_id);
      // use_nx_proxy: every call goes to <cloud_system_id>.relay.vmsproxy.com
      // (dw_utils); the server address is not dialled.
      if (site.use_nx_proxy)
        return {
          ...empty,
          cameraPorts: [],
          note: cloudId
            ? null
            : "This site's connector goes through the Nx cloud relay, and its cloud system ID isn't saved: the connector looks it up when it starts, so there is no relay address to test from here.",
          endpoints: cloudId
            ? [
                {
                  role: "relay",
                  address: `${cloudId}.relay.vmsproxy.com`,
                  ports: [443],
                  label: "Nx cloud relay",
                },
              ]
            : [],
        };
      return {
        ...empty,
        cameraPorts: [],
        endpoints: [
          ...withServer(ports(port(site.server_port, 7001)), "DW server"),
          ...(cloudId
            ? [
                {
                  role: "relay" as const,
                  address: `${cloudId}.relay.vmsproxy.com`,
                  ports: [443],
                  label: "Nx cloud relay",
                },
              ]
            : []),
        ],
      };
    }
    case "avigilon":
      return {
        ...empty,
        cameraPorts: [],
        endpoints: withServer(
          ports(port(site.server_port, 8443)),
          "Avigilon server"
        ),
      };
    case "genetec":
      // Web SDK (4590) and the media gateway's RTSP (654).
      return {
        ...empty,
        cameraPorts: [],
        endpoints: withServer(
          ports(port(site.server_port, 4590), port(site.stream_port, 654)),
          "Genetec server"
        ),
      };
    case "exacq":
      return {
        ...empty,
        cameraPorts: [],
        endpoints: withServer(
          ports(port(site.server_port, 80), port(site.stream_port, 554)),
          "Exacq server"
        ),
      };
    case "hikcentral":
      // The stream port comes back from HikCentral at run time.
      return {
        ...empty,
        cameraPorts: [],
        endpoints: withServer(
          ports(port(site.server_port, 80)),
          "HikCentral server"
        ),
      };
    case "luxriot":
    case "cortrol":
      return {
        ...empty,
        cameraPorts: [],
        endpoints: withServer(
          ports(port(site.server_port, 8080)),
          "Luxriot server"
        ),
      };
    case "video_insight":
      return {
        ...empty,
        cameraPorts: [],
        endpoints: withServer(
          ports(port(site.server_port, 9000)),
          "Video Insight server"
        ),
      };
    case "openeye":
      // OWS: the camera list, login and streams all go to OpenEye's cloud
      // for the NVR's device ID (openeye_factory); server_ip is not dialled.
      if (site.openeye_use_ows)
        return {
          ...empty,
          cameraPorts: [],
          note: `This site's connector reaches the NVR through OpenEye's cloud (OWS)${
            text(site.openeye_nvr_id)
              ? `, as device ${text(site.openeye_nvr_id)}`
              : ""
          }, so the check is OpenEye's cloud, not the NVR.`,
          endpoints: [
            {
              role: "relay",
              address: OPENEYE_OWS_HOST,
              ports: [443],
              label: "OpenEye cloud (OWS)",
            },
          ],
        };
      // Direct: the camera list from server_ip:server_port, streams from
      // http://server_ip/... (port 80).
      return {
        ...empty,
        cameraPorts: [],
        endpoints: withServer(
          ports(port(site.server_port, 80), 80),
          "OpenEye server"
        ),
      };
    case "salient":
      return {
        ...empty,
        cameraPorts: [],
        endpoints: [],
        recordingServerPorts: [80],
      };
    case "adpro":
      return {
        ...empty,
        cameraPorts: [554],
        endpoints: withServer(
          ports(site.server_port, port(site.stream_port, 554)),
          "Adpro server"
        ),
      };
    default:
      // RTSP family (rtsp, onvif_wireguard, alibi, …): each camera's address
      // on its RTSP port (554) or HTTP (80); the site address when set.
      // Any management or event server on the record is listed too.
      return {
        ...empty,
        endpoints: [
          ...withServer(
            ports(site.server_port, site.stream_port).length
              ? ports(site.server_port, site.stream_port)
              : [554, 80]
          ),
          ...managementServer(),
          ...eventServer(),
        ],
      };
  }
};

/**
 * The endpoint the connector dials first: the site server, then the
 * management server, else (cloud-only modes such as OpenEye OWS or the Nx
 * relay) the first endpoint.
 */
const primaryEndpoint = (site: EndpointSite | null): SiteEndpoint | null => {
  const { endpoints } = endpointProfile(site);
  return (
    endpoints.find(
      (e) => e.role === "server" || e.role === "management_server"
    ) ??
    endpoints[0] ??
    null
  );
};

/** The ports to test first for a site, from its profile. */
export const primaryPorts = (site: EndpointSite | null): number[] =>
  primaryEndpoint(site)?.ports ?? [];

/** The address the connector dials first for a site. */
export const primaryHost = (site: EndpointSite | null): string | null =>
  primaryEndpoint(site)?.address ?? null;
